import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, apiError } from "@/lib/api";
import { useToast } from "@/context/ToastContext";
import { useAuth } from "@/context/AuthContext";
import { Loader, Currency, Field } from "@/components/Atoms";
import {
  Shield, Users, Crown, Coins, Settings, Wrench, Ban, ScrollText, Map as MapIcon,
  UserCog, Layers, Gift, PackageOpen, Server, Lock, KeyRound, Flame, Calendar,
  Flag, Sparkles, RefreshCw
} from "lucide-react";



export function VipRenewals() {
  const toast = useToast();
  const [data, setData] = useState({ pending: [], count: 0 });
  const load = () => api.get("/admin/vip/renewals").then(({ data }) => setData(data)).catch(() => {});
  useEffect(() => { load(); }, []);
  const approve = async (uid) => {
    if (!window.confirm(`Approve VIP renewal for ${uid}? (+1 month)`)) return;
    try { await api.post("/admin/vip/approve", { user_id: uid }); toast.success("Approved + extended 1 month"); load(); }
    catch (e) { toast.error(apiError(e)); }
  };
  const decline = async (uid) => {
    if (!window.confirm(`Decline + REMOVE VIP from ${uid}? This deletes their VIP record.`)) return;
    try { await api.post("/admin/vip/decline", { user_id: uid }); toast.success("VIP revoked"); load(); }
    catch (e) { toast.error(apiError(e)); }
  };
  return (
    <div className="space-y-4 fade-up" data-testid="vip-renewals-tab">
      <div className="glass-card p-4">
        <h3 className="text-lg mb-1" style={{ fontFamily: "'Cinzel Decorative', serif" }}>VIP Renewals</h3>
        <p className="text-xs text-[var(--muted)]">
          Monthly VIPs whose expiration has triggered a pending renewal decision. Approve to extend +1 month, decline to revoke VIP entirely. Mirrors <code>/vip approve</code> / <code>/vip decline</code>.
        </p>
      </div>
      <div className="glass-card p-5" data-testid="vip-renewals-list">
        <h3 className="label mb-3">Pending ({data.count})</h3>
        {data.count === 0 && <p className="text-sm text-[var(--muted)]">No pending renewals.</p>}
        <ul className="divide-y divide-white/5">
          {data.pending.map((p) => {
            // Build the headline: prefer Discord username + (Web email)
            // when both are known, fall back gracefully.
            const discord = p.discord_username || null;
            const web     = p.web_username     || null;
            const hasAnyName = discord || web;
            return (
              <li key={p.user_id} className="flex items-center justify-between py-3 gap-3" data-testid={`vip-renewal-${p.user_id}`}>
                <div className="flex-1 min-w-0">
                  {hasAnyName ? (
                    <>
                      <div className="text-sm font-medium flex flex-wrap items-center gap-x-2 gap-y-0.5">
                        {discord && (
                          <span className="inline-flex items-center gap-1 text-[var(--bone)]" data-testid={`vip-renewal-discord-${p.user_id}`}>
                            <span className="text-[10px] text-indigo-300/80 uppercase tracking-wider">Discord</span>
                            <span>@{discord}</span>
                          </span>
                        )}
                        {discord && web && <span className="text-[var(--muted)] text-xs">·</span>}
                        {web && (
                          <span className="inline-flex items-center gap-1 text-[var(--bone)]" data-testid={`vip-renewal-web-${p.user_id}`}>
                            <span className="text-[10px] text-teal-300/80 uppercase tracking-wider">Web</span>
                            <span className="truncate max-w-[220px]">{web}</span>
                          </span>
                        )}
                      </div>
                      <div className="text-[10px] text-[var(--muted)] mt-0.5 font-mono break-all">{p.user_id}</div>
                    </>
                  ) : (
                    <div className="text-sm font-medium font-mono break-all">{p.user_id}</div>
                  )}
                  <div className="text-[10px] text-[var(--muted)] mt-0.5">
                    Expired: {p.expires_at} ({p.days_expired != null ? `${p.days_expired}d ago` : "?"}) · Type: {p.vip_type}
                  </div>
                </div>
                <div className="flex gap-2 shrink-0">
                  <button onClick={() => approve(p.user_id)} className="btn btn-sm" data-testid={`approve-${p.user_id}`}>Approve +1mo</button>
                  <button onClick={() => decline(p.user_id)} className="btn btn-sm btn-danger" data-testid={`decline-${p.user_id}`}>Decline</button>
                </div>
              </li>
            );
          })}
        </ul>
      </div>
    </div>
  );
}
